import { Employee } from '../types';
import { formatDate } from './dateUtils';

export const exportUtils = {
  // Convert employee data to CSV format
  convertEmployeesToCSV(employees: Employee[]): string {
    if (employees.length === 0) {
      return 'No active employees found';
    }

    // Define CSV headers
    const headers = [
      'Employee ID',
      'First Name',
      'Last Name',
      'Email',
      'Phone',
      'Address',
      'Position',
      'Department',
      'Hire Date',
      'Salary',
      'Hourly Rate',
      'Employment Type',
      'Gender',
      'Bank Account',
      'SSS Number',
      'PhilHealth Number',
      'Pag-IBIG Number',
      'TIN Number',
      'SSS Deduction',
      'PhilHealth Deduction',
      'Pag-IBIG Deduction',
      'Tax Deduction',
      'Status',
      'Created Date',
      'Updated Date'
    ];

    // Convert employees to CSV rows
    const rows = employees.map(employee => [
      employee.id || '',
      employee.first_name || '',
      employee.last_name || '',
      employee.email || '',
      employee.phone || '',
      employee.address || '',
      employee.position || '',
      employee.department || '',
      employee.hire_date ? formatDate(employee.hire_date) : '',
      employee.salary ? employee.salary.toString() : '',
      employee.hourly_rate ? employee.hourly_rate.toString() : '',
      employee.employment_type || '',
      employee.gender || '',
      employee.bank_account || '',
      employee.sss_number || '',
      employee.philhealth_number || '',
      employee.pagibig_number || '',
      employee.tin_number || '',
      employee.deduct_sss ? 'Yes' : 'No',
      employee.deduct_philhealth ? 'Yes' : 'No',
      employee.deduct_pagibig ? 'Yes' : 'No',
      employee.deduct_tax ? 'Yes' : 'No',
      employee.status || '',
      employee.created_at ? formatDate(employee.created_at) : '',
      employee.updated_at ? formatDate(employee.updated_at) : ''
    ]);

    // Escape CSV values (handle commas, quotes, newlines)
    const escapeCSVValue = (value: string): string => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    // Create CSV content
    const csvContent = [
      headers.map(escapeCSVValue).join(','),
      ...rows.map(row => row.map(cell => escapeCSVValue(cell.toString())).join(','))
    ].join('\n');

    return csvContent;
  },

  // Download CSV file
  downloadCSV(csvContent: string, filename: string): void {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  },

  // Generate filename with current date
  generateExportFilename(prefix: string = 'active_employees'): string {
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0]; // YYYY-MM-DD format
    const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '-'); // HH-MM-SS format
    return `${prefix}_${dateStr}_${timeStr}.csv`;
  },

  // Export active employees to CSV
  async exportActiveEmployees(employees: Employee[]): Promise<void> {
    try {
      const csvContent = this.convertEmployeesToCSV(employees);
      const filename = this.generateExportFilename('active_employees');
      this.downloadCSV(csvContent, filename);
    } catch (error) {
      console.error('Error exporting employees:', error);
      throw new Error('Failed to export employee data');
    }
  }
};